import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, SlidersHorizontal, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import ListingCard from "@/components/ListingCard";
import CommodityIcon from "@/components/CommodityIcon";
import { ListingCardSkeleton } from "@/components/Skeletons";

const ALL_COMMODITIES = [
  "All", "Crude Oil", "Natural Gas", "Gold", "Copper", "Iron Ore", "Coal", "Silver",
  "Lithium", "Maize", "Wheat", "Soybeans", "Coffee", "Sugar", "Platinum", "Chrome",
  "Vanadium", "Nickel", "Palladium", "Rice", "Salt", "Sunflower Oil", "Diamonds",
  "Bauxite", "Aluminium", "Manganese",
];
const SORTS = [
  { id: "recent", label: "Newest" },
  { id: "price_low", label: "Price ↑" },
  { id: "price_high", label: "Price ↓" },
  { id: "views", label: "Popular" },
];
const PAGE = 12;

export default function Browse() {
  const nav = useNavigate();
  const { symbol, currency } = useCurrency();
  const [q, setQ] = useState("");
  const [commodity, setCommodity] = useState("All");
  const [sort, setSort] = useState("recent");
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [loc, setLoc] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const debounceRef = useRef();

  const fetchPage = useCallback(async (skip = 0, append = false) => {
    append ? setMore(true) : setLoading(true);
    try {
      const params = { q: q || undefined, commodity, sort, skip, limit: PAGE,
        min_price: minP || undefined, max_price: maxP || undefined, location: loc || undefined };
      const { data } = await api.get("/listings", { params });
      setTotal(data.total);
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    } catch { toast.error("Failed to load listings"); }
    finally { setLoading(false); setMore(false); }
  }, [q, commodity, sort, minP, maxP, loc]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPage(0, false), 280);
    return () => clearTimeout(debounceRef.current);
  }, [fetchPage]);

  const toggleFav = async (l) => {
    try {
      const { data } = await api.post(`/favorites/${l.id}`);
      setItems((prev) => prev.map((x) => (x.id === l.id ? { ...x, is_favorite: data.favorited } : x)));
    } catch { toast.error("Please log in"); }
  };

  const activeFilters = (minP ? 1 : 0) + (maxP ? 1 : 0) + (loc ? 1 : 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="sticky top-0 z-30 glass px-4 pb-3 pt-4">
        <h1 className="font-display mb-3 text-2xl font-extrabold">Browse Market</h1>
        <div className="flex gap-2">
          <div className="glass flex flex-1 items-center gap-2 rounded-2xl px-4 py-3">
            <Search size={18} className="text-dim" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
                   placeholder="Search commodities, sellers…"
                   className="w-full bg-transparent text-sm outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
            {q && <button onClick={() => setQ("")}><X size={16} className="text-dim" /></button>}
          </div>
          <button onClick={() => setShowFilter(true)} data-testid="filter-btn"
                  className="pressable btn-accent relative flex h-12 w-12 items-center justify-center">
            <SlidersHorizontal size={18} />
            {activeFilters > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[11px] font-bold accent">{activeFilters}</span>
            )}
          </button>
        </div>
      </div>

      {/* Simple commodity chips (no expanding balloon) */}
      <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto px-4 pb-1">
        {ALL_COMMODITIES.map((c) => (
          <button key={c} onClick={() => setCommodity(c)} data-active={commodity === c} data-testid={`browse-chip-${c}`}
                  className="chip pressable shrink-0 px-4 py-2 text-sm font-semibold">{c}</button>
        ))}
      </div>

      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <p className="text-sm font-semibold text-dim">{total} result{total === 1 ? "" : "s"}</p>
        <div className="flex gap-1">
          {SORTS.map((s) => (
            <button key={s.id} onClick={() => setSort(s.id)} data-active={sort === s.id} data-testid={`sort-${s.id}`}
                    className="chip pressable px-2.5 py-1 text-xs font-semibold">{s.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 md:grid-cols-3">
        {loading
          ? [0, 1, 2, 3].map((i) => <ListingCardSkeleton key={i} />)
          : items.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} onOpen={() => nav(`/listing/${l.id}`)} onToggleFav={toggleFav} />
            ))}
      </div>

      {!loading && items.length === 0 && (
        <div className="px-4 py-20 text-center text-dim">
          <p className="font-semibold">No results found</p>
          <p className="mt-1 text-sm">Try a different search or filter</p>
        </div>
      )}

      {!loading && items.length < total && (
        <div className="px-4 py-5">
          <button onClick={() => fetchPage(items.length, true)} disabled={more} data-testid="load-more"
                  className="glass pressable flex w-full items-center justify-center gap-2 rounded-2xl py-3 font-bold">
            {more ? <Loader2 size={18} className="animate-spin" /> : "Load more"}
          </button>
        </div>
      )}

      {/* Filter bottom sheet */}
      <AnimatePresence>
        {showFilter && (
          <>
            <motion.div className="fixed inset-0 z-[60]" style={{ background: "rgba(0,0,0,0.5)" }}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setShowFilter(false)} />
            <motion.div className="fixed bottom-0 left-1/2 z-[70] w-full -translate-x-1/2 goe-surface"
                        style={{ maxWidth: 520, borderTopLeftRadius: 28, borderTopRightRadius: 28, borderTop: "1px solid var(--border)" }}
                        initial={{ y: "100%", x: "-50%" }} animate={{ y: 0, x: "-50%" }} exit={{ y: "100%", x: "-50%" }}
                        transition={{ type: "spring", stiffness: 320, damping: 32 }}>
              <div className="mx-auto mt-3 h-1.5 w-12 rounded-full" style={{ background: "var(--border)" }} />
              <div className="p-5">
                <h3 className="font-display mb-4 text-xl font-extrabold">Filters</h3>
                <label className="mb-1.5 block text-xs font-bold uppercase text-dim">Price range ({currency})</label>
                <div className="mb-4 flex items-center gap-3">
                  <input type="number" value={minP} onChange={(e) => setMinP(e.target.value)} placeholder={`${symbol()} Min`} data-testid="filter-min"
                         className="glass w-full rounded-xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
                  <span className="text-dim">–</span>
                  <input type="number" value={maxP} onChange={(e) => setMaxP(e.target.value)} placeholder={`${symbol()} Max`} data-testid="filter-max"
                         className="glass w-full rounded-xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
                </div>
                <label className="mb-1.5 block text-xs font-bold uppercase text-dim">Location</label>
                <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. Ghana, Zimbabwe" data-testid="filter-location"
                       className="glass mb-5 w-full rounded-xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
                <div className="flex gap-3">
                  <button onClick={() => { setMinP(""); setMaxP(""); setLoc(""); }} data-testid="filter-clear"
                          className="glass pressable flex-1 rounded-2xl py-3 font-bold">Clear</button>
                  <button onClick={() => setShowFilter(false)} data-testid="filter-apply"
                          className="btn-accent pressable flex flex-1 items-center justify-center gap-2 py-3 font-bold">
                    <Check size={18} /> Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
